import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useQuery} from '@apollo/client';
import {gql} from '@apollo/client';

import Empty from '../../../component/empty/Empty';
import Error from '../../../component/error/Error';
import InfiniteScroll from '../../../component/infiniteScroll/InfiniteScroll';
import Loading from '../../../component/loading/Loading';
import {useAuthStore} from '../../../store/useAuthStore';
import SortDropdown from '../../search/searchSort/SortDropdown';
import ReviewItem from './ReviewItem';

import './Review.css';

const REVIEW_LIMIT = 20;
const REVIEW_SORT = {
    NEW: {code: 'NEW', title: '최신순'},
    OLD: {code: 'OLD', title: '오래된 순'},
    HIGH: {code: 'HIGH', title: '별점 높은 순'},
    LOW: {code: 'LOW', title: '별점 낮은 순'},
};
const REVIEW_SORT_OPTIONS = Object.values(REVIEW_SORT);

// GraphQL queries
const SEE_USER_REVIEWS = gql`
    query SeeUserReviews(
        $sellerId: Int!
        $offset: Int
        $limit: Int
        $sort: ReviewSort
        $withDeletedReply: Boolean
    ) {
        seeUserReviews(
            id: $sellerId
            offset: $offset
            limit: $limit
            sort: $sort
            withDeletedReply: $withDeletedReply
        ) {
            id
            text
            rating
            createdAt
            updatedAt
            order_id
            user {
                id
                username
                nickname
                resizedSmallImage
                seller {
                    id
                    is_certified
                }
            }
            seller {
                is_certified
                user {
                    id
                    username
                    nickname
                    resizedSmallImage
                }
            }
            product {
                id
                title
                brand
                status
                resizedSmallImages
            }
            reviewReply {
                id
                text
                createdAt
                updatedAt
                deletedAt
            }
        }
    }
`;

function getReviewCount(seller, loadedCount) {
    return seller.review_count ?? seller.seller?.review_count ?? seller.reviews?.length ?? loadedCount;
}

function shouldLoadMore(loadedCount, reviewCount) {
    return loadedCount > 0 && loadedCount < reviewCount;
}

/**
 * Seller review tab component.
 */
export default function Review({ seller }) {
    const store = useAuthStore();
    const sellerId = parseInt(seller.id, 10);
    const [selectedSort, setSelectedSort] = useState(REVIEW_SORT.NEW.code);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasReachedEnd, setHasReachedEnd] = useState(false);
    // fetch the comments list
    const { loading, error, data, fetchMore } = useQuery(
        SEE_USER_REVIEWS,
        {
            variables: {
                offset: 0,
                limit: REVIEW_LIMIT,
                sellerId,
                sort: selectedSort,
                withDeletedReply: false
            },
            fetchPolicy: 'cache-and-network' // force cache refresh to show new reviews
        }
    );

    const reviews = data?.seeUserReviews || [];
    const reviewCount = getReviewCount(seller, reviews.length);
    const hasMore = !hasReachedEnd && shouldLoadMore(reviews.length, reviewCount);

    useEffect(() => {
        setHasReachedEnd(false);
    }, [sellerId, selectedSort]);

    useEffect(() => {
        if (data && (reviews.length < REVIEW_LIMIT || reviews.length >= reviewCount)) {
            setHasReachedEnd(true);
        }
    }, [data, reviewCount, reviews.length]);

    const loadMoreReviews = useCallback(() => {
        if (loading || isLoadingMore || !hasMore) {
            return;
        }

        setIsLoadingMore(true);
        const currentReviewCount = reviews.length;

        fetchMore({
            variables: {
                offset: currentReviewCount,
                limit: REVIEW_LIMIT,
                sellerId,
                sort: selectedSort,
                withDeletedReply: false
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) {
                    return previousResult;
                }

                const nextReviews = fetchMoreResult.seeUserReviews || [];

                return {
                    ...previousResult,
                    seeUserReviews: [
                        ...previousResult.seeUserReviews,
                        ...nextReviews
                    ]
                };
            }
        }).then(({ data: fetchMoreData }) => {
            const nextReviews = fetchMoreData?.seeUserReviews || [];
            const nextReviewCount = currentReviewCount + nextReviews.length;

            if (nextReviews.length < REVIEW_LIMIT || nextReviewCount >= reviewCount) {
                setHasReachedEnd(true);
            }
        }).finally(() => {
            setIsLoadingMore(false);
        });
    }, [fetchMore, hasMore, isLoadingMore, loading, reviewCount, reviews.length, selectedSort, sellerId]);

    if (loading && !data) {
        return <Loading />;
    } else if (error) {
        return <Error error={error} />;
    }

    // build the UI elements
    const reviewItems = reviews.length ?
        reviews.map(review => (
            <ReviewItem
                currentUserId={store.user?.id}
                key={review.id}
                review={review}
                seller={seller}
            />
        )):
        (
            <li className="Review-empty">
                <Empty>
                    구매후기가 없습니다
                </Empty>
            </li>
        );

    return (
        <section className="Review">
            <div className="Review-header">
                <h2 className="Review-title">
                    리뷰 {reviewCount}개
                </h2>
                <div className="Review-sort" aria-label="리뷰 정렬">
                    <SortDropdown
                        selectedSort={selectedSort}
                        onSortChange={setSelectedSort}
                        sortOptions={REVIEW_SORT_OPTIONS}
                    />
                </div>
            </div>
            <InfiniteScroll loading={loading || isLoadingMore} hasMore={hasMore} loadMore={loadMoreReviews}>
                <ul className="Review-list list-unstyled">
                    {reviewItems}
                </ul>
            </InfiniteScroll>
        </section>
    );
}
Review.propTypes = {
    seller: PropTypes.shape({
        id: PropTypes.string.isRequired,
        is_certified: PropTypes.bool,
        review_count: PropTypes.number,
        reviews: PropTypes.array,
        seller: PropTypes.shape({
            review_count: PropTypes.number
        }),
        user: PropTypes.shape({
            nickname: PropTypes.string.isRequired,
            resizedSmallImage: PropTypes.string
        })
    }).isRequired
};
